<template>
  <BaseModule
    v-bind="$props"
    class="social-module"
    data-module="social"
    :style="{
      '--bg-color': socialBackgroundColor,
    }"
  >
    <div class="content">
      <header
        v-if="sectionTitle || sectionSubtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="sectionTitle"
          class="tit tit-g"
        >
          {{ sectionTitle }}
        </component>
        <h3
          v-if="sectionSubtitle"
          class="sub"
        >
          {{ sectionSubtitle }}
        </h3>
      </header>

      <Warning
        v-if="items.length === 0 && editing"
        text="O módulo não possui nenhuma rede social adicionada"
      >
      </Warning>

      <div
        v-else
        class="lista-cards"
      >
        <EditorLink
          v-for="(item, index) in items"
          v-show="showButton(undefined, item.socialIcon)"
          :key="index"
          class="social-card"
          :style="{ backgroundColor: backgroundColorIcons }"
          :model="item.buttonUrl"
        >
          <component
            :is="item.socialIcon"
            :color="item.svgColor"
            :src="item.image"
            :alt="item.altText"
          />
          <div
            class="descricao-card"
            v-html="item.text"
          ></div>
        </EditorLink>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import Warning from '@/components/Warning.vue';
import IconFacebook from '@/components/icons/IconFacebook.vue';
import IconInstagram from '@/components/icons/IconInstagram.vue';
import IconYoutube from '@/components/icons/IconYoutube.vue';
import IconLinkedin from '@/components/icons/IconLinkedin.vue';
import IconTripAdvisor from '@/components/icons/IconTripAdvisor.vue';
import IconTwitter from '@/components/icons/IconTwitter.vue';
import IconSpotify from '@/components/icons/IconSpotify.vue';
import IconTikTok from '@/components/icons/IconTikTok.vue';
import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

const tinycolor = require('tinycolor2');

export default {
  name: 'Social',
  components: {
    BaseModule,
    EditorLink,
    Warning,
    facebookIcon: IconFacebook,
    instagramIcon: IconInstagram,
    youTubeIcon: IconYoutube,
    linkedinIcon: IconLinkedin,
    tripAdvisorIcon: IconTripAdvisor,
    twitterIcon: IconTwitter,
    spotifyIcon: IconSpotify,
    tiktokIcon: IconTikTok,
  },
  mixins: [showButton, base],
  props: {
    ...BaseModule.props,
    sectionTitle: {
      type: String,
      default: '',
    },
    sectionSubtitle: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    socialBackgroundColor: {
      type: String,
      default: '#ffffff',
    },
    textColor: {
      type: String,
      default: '#000000',
    },
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
    backgroundColorIcons() {
      const tColor = tinycolor(this.socialBackgroundColor);
      if (tColor.isDark()) {
        return tColor.brighten(8).toString();
      }
      return tColor.darken(8).toString();
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'sectionTitle',
                type: 'ShortText',
                label: 'Título',
                maxlength: 40,
              },
              {
                name: 'titleWeight',
                after: 'sectionTitle',
                isEnabled({ sectionTitle }) {
                  return sectionTitle;
                },
              },
              {
                name: 'sectionSubtitle',
                type: 'ShortText',
                label: 'Subtítulo',
              },
            ],
            toRemove: ['title', 'subtitle'], // migrate these fields
          },
          {
            name: 'socialLinks',
            label: 'Redes Sociais',
            fields: [
              {
                name: 'items',
                type: 'List',
                minItems: 2,
                maxItems: 10,
                itemLabel: 'Rede {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar rede',
                fields: [
                  {
                    name: 'socialIcon',
                    type: 'VueSelect',
                    label: 'Ícone',
                    default: '',
                    options: [
                      {
                        label: 'Instagram',
                        value: 'instagramIcon',
                      },
                      {
                        label: 'YouTube',
                        value: 'youTubeIcon',
                      },
                      {
                        label: 'Facebook',
                        value: 'facebookIcon',
                      },
                      {
                        label: 'LinkedIn',
                        value: 'linkedinIcon',
                      },
                      {
                        label: 'TripAdvisor',
                        value: 'tripAdvisorIcon',
                      },
                      {
                        label: 'Twitter',
                        value: 'twitterIcon',
                      },
                      {
                        label: 'Spotify',
                        value: 'spotifyIcon',
                      },
                      {
                        label: 'TikTok',
                        value: 'tiktokIcon',
                      },
                      {
                        label: 'Personalizado',
                        value: 'img',
                      },
                    ],
                  },
                  {
                    name: 'svgColor',
                    type: 'ColorPicker',
                    label: 'Cor do Ícone',
                    default: '#000000',
                    isEnabled() {
                      return this.socialIcon !== 'img';
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Ícone Personalizado',
                    helpText: 'Dimensões recomendadas: 72x72px',
                    isEnabled() {
                      return this.socialIcon === 'img';
                    },
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                    isEnabled() {
                      return this.socialIcon === 'img';
                    },
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'text',
                    type: 'ShortText',
                    label: 'Texto de apoio',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'socialBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
              },
            ],
            toRemove: ['backgroundColor', 'buttonColor', 'buttonTextColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.social-module {
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    max-width: 1300px;
  }

  .lista-cards {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    justify-content: center;
  }

  .social-card {
    text-align: center;
    width: 180px;
    padding: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--text-color);
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
      text-decoration: none;
    }

    .descricao-card {
      display: flex;
      flex-grow: 1;
      align-items: center;
      margin-top: 15px;
      font-size: $size-normal;
      line-height: 19px;
      max-width: 245px;
    }

    img {
      width: 72px;
      height: 72px;
    }
  }
}

@include media-breakpoint-down(sm) {
  .social-module {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .icon {
      ::v-deep svg {
        width: 60px;
        height: 60px;
      }
    }

    .lista-cards {
      gap: 14px;
    }

    .social-card {
      width: 160px;

      img {
        width: 60px;
        height: 60px;
      }
    }
  }
}
@include media-breakpoint-down(sm) {
  .social-module {
    .social-card {
      width: calc(50% - 7px);
    }
  }
}
</style>
